import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

import fiTranslations from "../locales/fi.json";
import enTranslations from "../locales/en.json";
import svTranslations from "../locales/sv.json";

export type SupportedLocale = "fi" | "en" | "sv";

export const SUPPORTED_LOCALES: SupportedLocale[] = ["fi", "sv", "en"];

export const DEFAULT_LOCALE: SupportedLocale = "fi";

export const normalizeLocale = (value?: string | null): SupportedLocale => {
  if (!value) return DEFAULT_LOCALE;
  const normalized = value.split("-")[0]?.toLowerCase();
  return SUPPORTED_LOCALES.includes(normalized as SupportedLocale)
    ? (normalized as SupportedLocale)
    : DEFAULT_LOCALE;
};

export const resolveLocale = (
  language?: string | null,
  resolved?: string | null,
): SupportedLocale => normalizeLocale(resolved ?? language);

const getQueryLocale = (): SupportedLocale | null => {
  if (typeof window === "undefined") return null;
  const queryLocale = new URLSearchParams(window.location.search).get("lang");
  if (!queryLocale) return null;
  const normalized = queryLocale.split("-")[0]?.toLowerCase();
  return SUPPORTED_LOCALES.includes(normalized as SupportedLocale)
    ? (normalized as SupportedLocale)
    : null;
};

i18n
  // Detect user language
  .use(LanguageDetector)
  // Pass the i18n instance to react-i18next
  .use(initReactI18next)
  // Initialize i18next
  .init({
    resources: {
      fi: {
        translation: fiTranslations,
      },
      en: {
        translation: enTranslations,
      },
      sv: {
        translation: svTranslations,
      },
    },
    fallbackLng: DEFAULT_LOCALE,
    supportedLngs: SUPPORTED_LOCALES,
    nonExplicitSupportedLngs: true,
    load: "languageOnly",
    defaultNS: "translation",
    interpolation: {
      escapeValue: false, // React already escapes values
    },
    detection: {
      // Order and from where user language should be detected
      order: ["localStorage", "navigator"],
      // Keys or params to lookup language from
      lookupLocalStorage: "i18nextLng",
      // Cache user language on
      caches: ["localStorage"],
    },
  });

const ensureLanguageSelected = () => {
  const queryLocale = getQueryLocale();
  if (queryLocale && i18n.language !== queryLocale) {
    void i18n.changeLanguage(queryLocale);
    return;
  }
  const selected = resolveLocale(i18n.language, i18n.resolvedLanguage);
  if (i18n.language !== selected) {
    void i18n.changeLanguage(selected);
  }
};

const updateHtmlLang = (language?: string | null) => {
  if (typeof document === "undefined") return;
  const selected = resolveLocale(language, i18n.resolvedLanguage);
  document.documentElement.lang = selected;
};

i18n.on("initialized", () => {
  ensureLanguageSelected();
  updateHtmlLang(i18n.language);
});
i18n.on("languageChanged", updateHtmlLang);
if (i18n.isInitialized) {
  ensureLanguageSelected();
  updateHtmlLang(i18n.language);
}

export default i18n;
